import styles from "../../assets/scss/modules/PublicListings.module.scss";
import React, {Fragment, HTMLAttributes, Key, useEffect} from 'react';
import {CtrInscriptions, CtrMunicipalites, CtrRegions, Maybe} from "../../graphql/generated/graphql-main.tsx";
import clsx from "clsx";
import {
    Autocomplete,
    AutocompleteRenderGetTagProps,
    Checkbox,
    Chip,
    FormControl,
    InputLabel,
    MenuItem,
    OutlinedInput,
    Select,
    useMediaQuery,
    useTheme
} from "@mui/material";
import {XTextField} from "../global/XTextField.tsx";
import useCtrRegions from "../../hooks/useCtrRegions.ts";
import {empty} from "../../utils/utils.ts";
import {useImmer} from "use-immer";
import {deleteValue} from "../../utils/array-utils.ts";
import useReactTexts from "../../hooks/useReactTexts.ts";

interface Props extends HTMLAttributes<HTMLDivElement> {
    listings: CtrInscriptions[];
    onFilter: (muns: string[]) => void;
    resetFlag: number;
}

const FiltersRegion = (props: Props) => {
    const {breakpoints} = useTheme();
    const {__} = useReactTexts();
    const is_sm = useMediaQuery(breakpoints.down("sm"));
    const {$regions, $municipalites, regName, munByCode, munName} = useCtrRegions();

    const [regions, setRegions] = useImmer<string[]>([]);
    const [municipalities, setMunicipalities] = useImmer<string[]>([]);

    // extract only relevant cities where at least one property exists in the city
    const usedMunicipalities: Set<string> = new Set(props.listings
        .map(it => it.MUN_CODE || ""));

    const $finalMunicipalities: CtrMunicipalites[] = $municipalites
        .filter(it => usedMunicipalities.has(it.CODE!))
        .filter(it => empty(regions) ? true : regions.includes(it.REGION_CODE ?? ""))
        .toSorted((a, b) => {
            if (a.CODE == b.CODE) return 0;
            return (munName(a.CODE) || "") > (munName(b.CODE) || "") ? 1 : -1;
        });

    // extract unique regions from the set of used municipalities
    const usedRegions: Set<string> = new Set(props.listings
        .map(it => munByCode(it.MUN_CODE)?.REGION_CODE || ""));

    const $finalRegions: CtrRegions[] = $regions
        .filter(it => usedRegions.has(it.CODE!))
        .toSorted((a, b) => {
            if (a.CODE == b.CODE) return 0;
            return (regName(a.CODE) || "") > (regName(b.CODE) || "") ? 1 : -1;
        });

    // given region code, count number of properties across all municipalities of this region
    const countListingsByRegion = (code: Maybe<string>) => {
        const _muns = $municipalites.filter(it => it.REGION_CODE == code).map(it => it.CODE);
        return props.listings.filter(it => _muns.includes(it.MUN_CODE)).length;
    }
    // given mun code, count number of properties in that mun
    // const countListingsByMun = (code: Maybe<string>) => {
    //     return props.listings.filter(it => it.MUN_CODE == code).length;
    // }

    const deleteRegion = (code: Maybe<string>) => {
        setRegions(draft => {
            deleteValue(draft, code);
        });
    }
    const deleteMunicipality = (code: Maybe<string>) => {
        setMunicipalities(draft => {
            deleteValue(draft, code);
        })
    }

    // return proper set of municipality codes based on region and/or municipality selection
    useEffect(() => {
        let _muns: string[] = [];
        // if specific municipalities are selected, return array with those specific municipalities
        if (!empty(municipalities)) {
            _muns = [...municipalities];
        }
        // if only regions are selected, return muns already pre-filtered by selected regions
        else if (!empty(regions)) {
            _muns = $finalMunicipalities.map(it => it.CODE || "");
        }

        // send final selection to calling component to filter (or [] if both region & mun are unset)
        props.onFilter(_muns);
    }, [regions, municipalities]);

    const mobileRegionSelector = () => {
        return <FormControl className={clsx("w-full mobile", styles.dd_select)}>
            <InputLabel>{__("Toutes les régions")}</InputLabel>
            <Select
                variant={"outlined"}
                size={"medium"}
                multiple
                MenuProps={{
                    className: styles.popper_select
                }}
                input={<OutlinedInput label={__("Toutes les régions")}/>}
                value={regions}
                onChange={event => {
                    console.info(`event`, event.target.value);
                    setRegions((event.target.value ?? []) as string[]);
                    setMunicipalities([]);
                }}
                renderValue={(value: string[]) => {
                    const _labels = value.map(code => regName(code ?? null)).join(', ');
                    return <span className={styles.dd_select_tags}>{_labels}</span>
                }}
            >
                {$finalRegions.map((option) => {
                    const _selected = regions.includes(option.CODE!);
                    return <MenuItem
                        key={`region-${option.CODE}`}
                        value={option.CODE!}
                        className={clsx("!justify-between", {"!bg-merino": _selected})}
                    >
                        <div className={"flex items-center space-x-2"}>
                            <Checkbox className={"p-0 m-0"} checked={_selected}/>
                            <span>{regName(option.CODE ?? null)}</span>
                        </div>
                        <Chip className={"rounded bg-merino"} size={"small"}
                              label={countListingsByRegion(option.CODE ?? null)}
                        ></Chip>
                    </MenuItem>
                })}
            </Select>
        </FormControl>
    }
    const mobileMunicipalitySelector = () => {
        return <FormControl className={clsx("w-full mobile", styles.dd_select)}>
            <InputLabel>{__("Toutes les municipalités")}</InputLabel>
            <Select
                variant={"outlined"}
                size={"medium"}
                multiple
                MenuProps={{
                    className: styles.popper_select
                }}
                input={<OutlinedInput label={__("Toutes les municipalités")}/>}
                value={municipalities}
                onChange={event => {
                    console.info(`municipalities`, event.target.value);
                    setMunicipalities((event.target.value ?? []) as string[]);
                }}
                renderValue={(value: string[]) => {
                    const _labels = value.map(code => munName(code ?? null)).join(', ');
                    return <span className={styles.dd_select_tags}>{_labels}</span>
                }}
            >
                {$finalMunicipalities.map((option) => {
                    const _selected = municipalities.includes(option.CODE!);
                    return <MenuItem
                        key={`municipality-${option.REGION_CODE}-${option.CODE}`}
                        value={option.CODE!}
                        className={clsx("space-x-2", {"!bg-merino": _selected})}
                    >
                        <Checkbox className={"p-0 m-0"} checked={_selected}/>
                        <span>{munName(option.CODE ?? null)}</span>
                    </MenuItem>
                })}
            </Select>
        </FormControl>
    }
    const desktopRegionSelector = () => {
        return <Autocomplete<CtrRegions, boolean, boolean>
            size={"small"}
            className={clsx("desktop", styles.dd_autocomplete)}
            classes={{
                popper: styles.popper_autocomplete,
            }}
            disableCloseOnSelect={true}
            multiple={true}
            options={$finalRegions}
            value={$finalRegions.filter(it => regions.includes(it.CODE!))}
            getOptionKey={option => option.CODE!}
            getOptionLabel={option => regName(option.CODE ?? null) ?? ""}
            isOptionEqualToValue={(option, value) => option.CODE == value.CODE}
            onChange={(event, choices: Maybe<CtrRegions[]>) => {
                // whenever we change regions, always reset municipalities to prevent hidden selection
                setRegions(choices?.map(it => it.CODE!) ?? []);
                setMunicipalities([]);
            }}
            renderInput={params => <XTextField {...params} label={__("Toutes les régions")}/>}
            renderOption={(props1: HTMLAttributes<HTMLLIElement> & { key: Key }, option) => {
                const _selected = props1["aria-selected"] == true;
                return <li {...props1} key={props1.key} className={clsx(props1.className,
                    "!justify-between", {"!bg-merino": _selected})}>
                    <div className={"flex items-center space-x-2"}>
                        <Checkbox className={"p-0 m-0"} checked={_selected}/>
                        <span>{regName(option.CODE ?? null)}</span>
                    </div>
                    <Chip className={"rounded bg-merino"} size={"small"}
                          label={countListingsByRegion(option.CODE ?? null)}
                    ></Chip>
                </li>
            }}
            renderTags={(list, getTagProps: AutocompleteRenderGetTagProps) => {
                const _labels = list.map(option => regName(option.CODE ?? null)).join(', ');
                const _props = getTagProps({index: 0});
                return <span {..._props} key={_props.key} className={styles.dd_autocomplete_tags}>
                    {_labels}
                </span>
            }}
        ></Autocomplete>
    }
    const desktopMunicipalitySelector = () => {
        return <Autocomplete<CtrMunicipalites, boolean, boolean>
            size={"small"}
            className={clsx("", styles.dd_autocomplete)}
            classes={{
                popper: styles.popper_autocomplete,
            }}
            disableCloseOnSelect={true}
            multiple={true}
            options={$finalMunicipalities}
            value={$finalMunicipalities.filter(it => municipalities.includes(it.CODE!))}
            getOptionKey={option => option.CODE!}
            getOptionLabel={option => munName(option.CODE ?? null) ?? ""}
            isOptionEqualToValue={(option, value) => option.CODE == value.CODE}
            onChange={(event, choices: Maybe<CtrMunicipalites[]>) => {
                setMunicipalities(choices?.map(it => it.CODE!) ?? []);
            }}
            renderInput={params => <XTextField {...params} label={__("Toutes les municipalités")}/>}
            renderOption={(props1: HTMLAttributes<HTMLLIElement> & { key: Key }, option) => {
                const _selected = props1["aria-selected"] == true;
                return <li {...props1} key={props1.key} className={clsx(props1.className,
                    "space-x-2", {"!bg-merino": _selected})}>
                    <Checkbox className={"p-0 m-0"} checked={_selected}/>
                    <span>{munName(option.CODE ?? null)}</span>
                </li>
            }}
            renderTags={(list, getTagProps: AutocompleteRenderGetTagProps) => {
                const _labels = list.map(option => munName(option.CODE ?? null)).join(', ');
                const _props = getTagProps({index: 0});
                return <span {..._props} key={_props.key} className={styles.dd_autocomplete_tags}>{_labels}</span>
            }}
        ></Autocomplete>
    }

    // when reset flag is updated, this triggers resetting this filter
    useEffect(() => {
        if (props.resetFlag == 0) return;
        setRegions([]);
        setMunicipalities([]);
    }, [props.resetFlag]);

    return <Fragment>

        {/* region selector */}
        <div className={clsx("w-full", props.className)}>
            {is_sm ? mobileRegionSelector() : desktopRegionSelector()}

            {/* list user selection as deletable chips */}
            {!empty(regions) && <div className={styles.filter_chips}>
                {regions.map(region => <Chip
                    key={`chip4region-${region}`}
                    size={"small"}
                    label={regName(region)}
                    onDelete={event => deleteRegion(region)}
                ></Chip>)}
            </div>}
        </div>

        {/* municipality selector */}
        <div className={clsx("w-full", props.className)}>
            {is_sm ? mobileMunicipalitySelector() : desktopMunicipalitySelector()}

            {/* list user selection as deletable chips */}
            {!empty(municipalities) && <div className={styles.filter_chips}>
                {municipalities.map(mun => <Chip
                    key={`chip4mun-${mun}`}
                    size={"small"}
                    label={munName(mun)}
                    onDelete={event => deleteMunicipality(mun)}
                ></Chip>)}
            </div>}
        </div>

    </Fragment>
}

export default FiltersRegion;